import * as React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, ModalVariant } from '@patternfly/react-core';
import { PlayIcon, ClockIcon, ArrowRightIcon, EnvelopeIcon } from '@patternfly/react-icons';
import { getSubscription } from '@app/utils/utils';
import {
  HELP_VIDEOS, HelpVideo, videoUrl, posterUrl, captionsUrl, formatDuration,
} from '@app/Help/helpVideos';

const GOLD = '#d4af37';
const NAVY = '#0f172a';
const MUTED = '#64748b';

const styles: { [k: string]: React.CSSProperties } = {
  page: { padding: '32px 40px', maxWidth: 1200, margin: '0 auto' },
  h1: { fontSize: 28, fontWeight: 700, color: NAVY, margin: 0 },
  sub: { color: MUTED, marginTop: 6, marginBottom: 28 },
  cta: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20,
    flexWrap: 'wrap', background: 'linear-gradient(135deg, #1e1b4b 0%, #4c1d95 100%)',
    border: '1px solid rgba(212,175,55,.4)', borderRadius: 16, padding: '20px 26px',
    marginBottom: 28,
  },
  ctaTitle: { color: '#fff', fontSize: 20, fontWeight: 700, margin: 0 },
  ctaSub: { color: 'rgba(255,255,255,.82)', marginTop: 4, fontSize: 14 },
  ctaBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 8, background: GOLD, color: NAVY,
    border: 'none', borderRadius: 10, padding: '12px 22px', fontSize: 15, fontWeight: 700,
    cursor: 'pointer', whiteSpace: 'nowrap',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 20,
  },
  card: {
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14,
    overflow: 'hidden', cursor: 'pointer', transition: 'transform .15s ease, box-shadow .15s ease',
    display: 'flex', flexDirection: 'column',
  },
  posterWrap: { position: 'relative', aspectRatio: '16 / 9', background: NAVY },
  poster: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  playBadge: {
    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
    justifyContent: 'center', color: '#fff', fontSize: 34, textShadow: '0 2px 8px rgba(0,0,0,.5)',
  },
  durationChip: {
    position: 'absolute', bottom: 8, right: 8, background: 'rgba(15,23,42,.85)',
    color: '#fff', fontSize: 12, padding: '3px 8px', borderRadius: 6,
    display: 'inline-flex', alignItems: 'center', gap: 4,
  },
  body: { padding: '14px 16px 16px', flex: 1, display: 'flex', flexDirection: 'column' },
  step: { color: GOLD, fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase' },
  title: { color: NAVY, fontSize: 17, fontWeight: 600, margin: '4px 0 6px' },
  desc: { color: MUTED, fontSize: 14, lineHeight: 1.45, flex: 1 },
  prereq: {
    marginTop: 10, alignSelf: 'flex-start', background: '#fef3c7', color: '#92400e',
    fontSize: 12, fontWeight: 600, padding: '3px 9px', borderRadius: 999,
  },
  modalDesc: { color: MUTED, marginTop: 14, fontSize: 14, lineHeight: 1.5 },
  video: { width: '100%', borderRadius: 8, background: '#000', display: 'block' },
  contact: {
    marginTop: 36, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14,
    padding: '24px 26px',
  },
  contactTitle: { fontSize: 20, fontWeight: 700, color: NAVY, margin: 0 },
  contactSub: { color: MUTED, marginTop: 6, marginBottom: 16, fontSize: 14, lineHeight: 1.5 },
  contactLink: {
    display: 'inline-flex', alignItems: 'center', gap: 8, color: GOLD,
    fontSize: 16, fontWeight: 700, textDecoration: 'none',
  },
};

const HelpCenter: React.FunctionComponent<any> = () => {
  const [active, setActive] = useState<HelpVideo | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const [needsPlan, setNeedsPlan] = useState(false);
  const navigate = useNavigate();
  const videos = [...HELP_VIDEOS].sort((a, b) => a.order - b.order);

  useEffect(() => {
    let cancelled = false;
    getSubscription()
      .then((sub) => {
        if (cancelled) return;
        const hasActive = !!sub && (sub.status === 'ACTIVE' || sub.status === 'TRIALING');
        setNeedsPlan(!hasActive);
      })
      .catch(() => { /* backend unreachable — just don't show the CTA */ });
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={styles.page}>
      <h1 style={styles.h1}>Help Center</h1>
      <p style={styles.sub}>
        Getting Started — short videos that walk you through the platform, in order.
      </p>

      {needsPlan && (
        <div style={styles.cta}>
          <div>
            <p style={styles.ctaTitle}>Ready to launch your first campaign?</p>
            <p style={styles.ctaSub}>Choose a plan to unlock AI ads, uploads, and campaigns.</p>
          </div>
          <button type="button" style={styles.ctaBtn} onClick={() => navigate('/plan')}>
            Choose a plan <ArrowRightIcon />
          </button>
        </div>
      )}

      <div style={styles.grid}>
        {videos.map((v) => (
          <div
            key={v.id}
            role="button"
            tabIndex={0}
            onClick={() => setActive(v)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActive(v); } }}
            onMouseEnter={() => setHovered(v.id)}
            onMouseLeave={() => setHovered(null)}
            style={{
              ...styles.card,
              transform: hovered === v.id ? 'translateY(-3px)' : 'none',
              boxShadow: hovered === v.id ? '0 10px 24px rgba(15,23,42,.14)' : '0 1px 2px rgba(15,23,42,.06)',
            }}
          >
            <div style={styles.posterWrap}>
              <img src={posterUrl(v)} alt="" loading="lazy" style={styles.poster} />
              <div style={styles.playBadge}><PlayIcon /></div>
              <span style={styles.durationChip}><ClockIcon /> {formatDuration(v.durationSec)}</span>
            </div>
            <div style={styles.body}>
              <span style={styles.step}>Step {v.order}</span>
              <div style={styles.title}>{v.title}</div>
              <div style={styles.desc}>{v.description}</div>
              {v.prereq && <span style={styles.prereq}>{v.prereq}</span>}
            </div>
          </div>
        ))}
      </div>

      <div style={styles.contact}>
        <h2 style={styles.contactTitle}>Contact us</h2>
        <p style={styles.contactSub}>
          Still have questions or need a hand? Our team is here to help — reach out and we&apos;ll get back to you as soon as we can.
        </p>
        <a style={styles.contactLink} href="mailto:contact@advolt.app">
          <EnvelopeIcon /> contact@advolt.app
        </a>
      </div>

      <Modal
        variant={ModalVariant.large}
        title={active ? `Step ${active.order}: ${active.title}` : ''}
        isOpen={!!active}
        onClose={() => setActive(null)}
        aria-label="Tutorial video player"
      >
        {active && (
          <>
            {/* key forces a fresh <video> per selection so the source reloads */}
            <video
              key={active.id}
              style={styles.video}
              controls
              autoPlay
              playsInline
              preload="metadata"
              poster={posterUrl(active)}
            >
              <source src={videoUrl(active)} type="video/mp4" />
              {active.hasCaptions && (
                <track kind="captions" src={captionsUrl(active)} srcLang="en" label="English" default />
              )}
              Your browser does not support the video tag.
            </video>
            <p style={styles.modalDesc}>
              {active.description}{active.prereq ? ` · ${active.prereq}` : ''}
            </p>
          </>
        )}
      </Modal>
    </div>
  );
};

export { HelpCenter };
